import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, message } from "antd";
import { CloseOutlined, WarningFilled } from "@ant-design/icons";
import constants from "../../constants/constants";
import { COOKIE, createCookie, deleteCookie, getCookie } from "../../services/cookie";
import { getParentDomain } from "../../globalFunctions/GlobalFunctions";
import { fetchAdminApi, fetchApi } from "../../services/api";

const WARNING_BEFORE_EXPIRY_MS = 10 * 60 * 1000; /* Show popup 10 minutes before expiry. */
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000; /* Check token changes every 30 seconds. */

const decodeJwtPayload = (token) => {
	if (!token || typeof token !== "string") return null;
	const payload = token.split(".")[1];
	if (!payload) return null;
	try {
		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
		return JSON.parse(window.atob(padded));
	} catch {
		return null;
	}
};
const getTokenExpiryMs = (token) => {
	const payload = decodeJwtPayload(token);
	return payload?.exp ? Number(payload.exp) * 1000 : null;
};
const getRedirectUrl = (userType) => {
	if (userType === "admin") {
		return `${constants.BaseUrl || ""}/admin-login`;
	}else{
		if (process.env.REACT_APP_ENV === "qa" || process.env.REACT_APP_ENV === "dev"){
			return `${constants.BaseUrl || ""}/dashboard`;
		}else if(process.env.REACT_APP_ENV === "staging"){
			return constants.StagingWebsiteURL;
		}else{
			return constants.LiveWebsiteURL;
		}
	}
};
const getNewTokenFromResponse = (response) => {
	const data = response?.token;
	return data;
};
const clearSessionCookies = (userType) => {
	const parentDomain = userType === "student" ? getParentDomain() : null;
	const cookieNames = [COOKIE.UserId, COOKIE.Token, COOKIE.FirstName, COOKIE.LastName];
	cookieNames.forEach((name) => {
		deleteCookie(name, "/");
		if (parentDomain) {
			deleteCookie(name, "/", parentDomain);
		}
	});
	sessionStorage.clear();
};

const formatRemainingTime = (milliseconds) => {
	const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes} min ${seconds} sec`;
};

const SessionTimeoutManager = ({ userType = "student" }) => {
	const [warningVisible, setWarningVisible] = useState(false);
	const [remainingMs, setRemainingMs] = useState(0);
	const [refreshing, setRefreshing] = useState(false);
	const tokenRef = useRef(null);
	const countdownTimerRef = useRef(null);
	const warningDismissedRef = useRef(false);
	const isLoggingOutRef = useRef(false);

	const redirectUrl = useMemo(() => getRedirectUrl(userType), [userType]);

	const clearTimers = useCallback(() => {
		window.clearInterval(countdownTimerRef.current);
	}, []);

	const logout = useCallback(async () => {
		if (isLoggingOutRef.current) return;
		isLoggingOutRef.current = true;
		try {
			const userId = getCookie(COOKIE.UserId);

			if (userId) {
				if (userType === "student") {
					await fetchApi(`/user-logout/${userId}`,"post",{ is_logged_in: 0 });
				} else {
					await fetchAdminApi(`/logout/${userId}`,"post",{ is_logged_in: 0 });
				}
			}
		} catch (error) {
			console.error("Logout API failed", error);
		} finally {
			clearTimers();
			clearSessionCookies(userType);
			window.location.href = redirectUrl;
		}
	}, [clearTimers, redirectUrl, userType]);

	const startCountdown = useCallback((passedToken = null) => {
		window.clearInterval(countdownTimerRef.current);
		const updateCountdown = () => {
			const latestToken = passedToken || getCookie(COOKIE.Token);
			// token removed in another tab
			if (!latestToken) {
				setWarningVisible(false);
				window.clearInterval(countdownTimerRef.current);
				logout();
				return;
			}
			const expiresAtMs = getTokenExpiryMs(latestToken);
			// invalid token
			if (!expiresAtMs) {
				setWarningVisible(false);
				window.clearInterval(countdownTimerRef.current);
				logout();
				return;
			}
			const remaining = expiresAtMs - Date.now();
			// expired
			if (remaining <= 0) {
				setRemainingMs(0);
				setWarningVisible(false);
				window.clearInterval(countdownTimerRef.current);
				logout();
				return;
			}
			// update timer
			setRemainingMs(remaining);

			if (
				remaining <= WARNING_BEFORE_EXPIRY_MS &&
				!warningDismissedRef.current
			) {
				setWarningVisible(true);
			} else if (
				remaining > WARNING_BEFORE_EXPIRY_MS
			) {
				setWarningVisible(false);
				warningDismissedRef.current = false;
			}
		};
		// run immediately
		updateCountdown();
		// continue checking every second
		countdownTimerRef.current = window.setInterval(updateCountdown, 1000);
	}, [logout]);

	const handleTokenChange = useCallback(() => {
		const token = getCookie(COOKIE.Token);
		// no token
		if (!token) {
			return;
		}
		// token unchanged
		if (token === tokenRef.current) {
			return;
		}
		tokenRef.current = token;
		startCountdown();
	}, [logout, startCountdown]);

	useEffect(() => {
		handleTokenChange();
		const tokenPoller = window.setInterval(
			handleTokenChange,
			TOKEN_CHECK_INTERVAL_MS
		);
		// FIX inactive tab issue
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				handleTokenChange();
			}
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			window.clearInterval(tokenPoller);
			document.removeEventListener( "visibilitychange", handleVisibilityChange);
			clearTimers();
		};
	}, [clearTimers, handleTokenChange]);

	const extendSession = async () => {
		const token = getCookie(COOKIE.Token);
		if (!token) {
			logout();
			return;
		}
		setRefreshing(true);
		try {
			const response =  userType === "student" ? await fetchApi("/refresh-token", "post", { user_id: getCookie(COOKIE.UserId)}) :await fetchAdminApi("/refresh-token", "post", { user_id: getCookie(COOKIE.UserId) });
			const newToken = response.code === 200 ? getNewTokenFromResponse(response) : null;
			if (!newToken) {
				throw new Error("Refresh token API did not return a token");
			}
			const domain = userType === "student" ? getParentDomain() : undefined;
			deleteCookie(COOKIE.Token, "/", domain);
			createCookie(COOKIE.Token, newToken, null, "/", domain);
			tokenRef.current = newToken;
			warningDismissedRef.current = true;
			setWarningVisible(false);
			startCountdown(newToken);
			message.success("Session extended successfully.");
		} catch {
			message.error("Unable to extend session. Please log in again.");
			logout();
		} finally {
			setRefreshing(false);
		}
	};

	return (
		<Modal
			centered
			open={warningVisible}
			width={624}
			className="session-timeout-modal"
			closable
			onCancel={() => {
				setWarningVisible(false);
				warningDismissedRef.current = true;
			}}
			title={(
				<>
					<div className="flex items-center gap-2 text-xl font-bold">
						<WarningFilled aria-hidden="true" className="text-[#faad14] text-[18px]" />
						<span>Session Timeout Warning</span>
					</div>
				</>
			)}
			closeIcon={<CloseOutlined aria-hidden="true" />}
			footer={[
				<Button key="continue" type="primary" className="!bg-blue-700 text-white" loading={refreshing} onClick={extendSession}>
					Continue Session
				</Button>,
				<Button key="ok" type="primary" className="!bg-green-700 hover:bg-green-600 text-white"
					onClick={() => {
						setWarningVisible(false);
						warningDismissedRef.current = true;
					}}
				>
					OK
				</Button>
			]}
		>
			<div className="p-1">
				<p className="text-base mb-1">Your session will expire automatically in {formatRemainingTime(remainingMs)}.</p>
				<p className="text-base">Click <strong>Continue Session</strong> to extend your session for another 24 hours.</p>
			</div>
		</Modal>
	);
};

export default SessionTimeoutManager;
